import React from 'react';
import { Check, X } from 'lucide-react';

/**
 * The frame of an interactive card in a bot chat, tinted like the bot's chat bubble
 * (Rakazo, OpenMausBot): an icon tile, who asks and what, the body, and the actions at
 * the bottom. A card that waits for the person carries an orange "waiting for you" hint.
 * large: a bigger title, for a question the person answers (OpenMausBot's question card).
 */
export const CardShell = ({ icon: Icon, eyebrow, title, children, footer, waiting, compact, large, testid, className = '' }) => (
  <div data-testid={testid}
    // In a chat it is tinted like the bot's bubble; compact (Home, the Bots page, the side
    // panel) it is a card like the cards around it.
    className={`overflow-hidden ${compact ? 'rounded-xl border border-border bg-accent/60' : 'rounded-[18px] bg-bubble-bot'} ${className}`}>
    <div className={`flex items-start gap-3 ${compact ? 'px-3 pt-3' : 'px-4 pt-4'}`}>
      <span className="w-8 h-8 rounded-lg bg-honey-soft text-amber-700 dark:text-amber-300 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <div className="min-w-0 flex-1 pt-px">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground truncate">{eyebrow}</span>
          {waiting && <span className="ml-auto flex-shrink-0 w-1.5 h-1.5 rounded-full bg-attention" aria-hidden="true" />}
        </div>
        {title && (
          <div className={`text-foreground break-words mt-0.5 ${large ? 'text-[15px] font-semibold leading-snug' : 'text-sm font-medium leading-snug'}`}>
            {title}
          </div>
        )}
      </div>
    </div>
    {children && <div className={compact ? 'px-3 pt-2.5' : 'px-4 pt-3'}>{children}</div>}
    {footer ? (
      <div className={`flex flex-wrap items-center gap-2 ${compact ? 'px-3 py-3' : 'px-4 py-3.5'}`}>{footer}</div>
    ) : <div className={compact ? 'h-3' : 'h-4'} />}
  </div>
);

/** A label and a value in a card (When, Next, Repeats...). */
export const CardRow = ({ label, children }) => (
  <div className="flex gap-3 text-[13px] py-1">
    <span className="w-20 flex-shrink-0 text-muted-foreground">{label}</span>
    <span className="min-w-0 flex-1 text-foreground">{children}</span>
  </div>
);

/** A decided card, folded to one line: what it was and how it ended. */
export const ResolvedCard = ({ icon: Icon, text, outcome, ok = true, action, testid }) => (
  <div data-testid={testid}
    className="flex items-center gap-2.5 rounded-2xl bg-bubble-bot/70 px-3.5 py-2.5 text-[13px] min-w-0">
    <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
    <span className="min-w-0 flex-1 truncate text-foreground/90">{text}</span>
    <span className={`flex-shrink-0 inline-flex items-center gap-1 text-xs ${ok ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'}`}>
      {ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}{outcome}
    </span>
    {action}
  </div>
);

/** The computer a card is about: its project's, or for a chat outside projects the person's
 * own (backend agents/places.py), where the card has no project and only a place. */
export const placeOf = (approval) => approval.place_id || approval.project_id;

/** What became of a decided card. One the person never answered, because the work was
 * stopped first, says so: it was neither handed back nor declined. */
export const outcomeOf = (approval, t, done, declined = t('bots.cards.declined')) => (
  approval.status === 'cancelled' ? t('bots.cards.stopped') : approval.status === 'approved' ? done : declined);

export const primaryBtn = 'h-8 px-3.5 rounded-lg bg-amber-500 text-honey-ink text-[13px] font-semibold hover:bg-amber-400 disabled:opacity-40 inline-flex items-center gap-1.5 transition-colors';
export const secondaryBtn = 'h-8 px-3 rounded-lg border border-input text-[13px] text-foreground hover:bg-accent disabled:opacity-40 inline-flex items-center gap-1.5 transition-colors';
export const ghostBtn = 'h-8 px-2.5 rounded-lg text-[13px] text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-40 inline-flex items-center gap-1.5 transition-colors';
export const dangerBtn = 'h-8 px-3.5 rounded-lg bg-red-600 text-white text-[13px] font-semibold hover:bg-red-500 disabled:opacity-40 inline-flex items-center gap-1.5 transition-colors';
export const inputCls = 'w-full h-8 rounded-lg border border-input bg-background px-2.5 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-amber-500/60';

/** The tool's result stored on the decided card (JSON), or null. */
export const parseResult = (approval) => {
  const raw = approval?.result;
  if (!raw || raw.startsWith('Error')) return null;
  try { return JSON.parse(raw); } catch { return null; }
};

export const resultError = (approval) => {
  const raw = approval?.result || '';
  return raw.startsWith('Error') ? raw.replace(/^Error:\s*/, '') : null;
};
